import {
  Component,
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import { DateTime } from 'luxon'
import {
  Button,
  Card,
  Container,
  Dimmer,
  Divider,
  Grid,
  Header,
  Label,
  Loader,
  Menu,
  Modal,
  Popup,
  Tab,
  Table,
} from 'semantic-ui-react'
import {
  faCircleCheck,
  faCircleXmark,
  faBolt,
} from '@fortawesome/pro-solid-svg-icons'
import { adminFetchSingleUser } from '@/actions/admin/adminAllUsersActions'
import { fetchTransactionCounts } from '@/actions/adminActions'
import { fetchAdminUsersDocuments } from '@/actions/admin/userDocumentsActions'
import {
  fetchReconciliationCapabilities,
  fetchStaleTransactionCountsForReport,
  fetchTransactionCountsSinceLastReconciliation,
  fetchUserFinancialAccounts,
  type PeriodState,
  type ReconciliationCapabilities,
  ReconciliationUnavailableReason,
} from '@/actions/admin/adminFinancialAccountActions'
import {
  fetchAdminUsersBookkeepingReports,
  updateUsersBookkeepingReportStatus,
  FETCH_UPDATE_BOOKKEEPING_REPORT_STATUS_KEY,
} from '@/actions/admin/bookkeepingReportActions'
import { BookkeepingStatusLabel } from '../user-record/BookkeepingStatusLabel'
import BookkeepingStatusCard from './BookkeepingStatusCard'
import { PaginatedTransactionTable } from '../paginated-transaction-table/PaginatedTransactionTable'
import ProfitAndLossTable from './ProfitAndLossTable'
import { UserRecordNotes } from '../user-record/UserRecordNotes'
import ReconciliationModal, {
  ReconciliationModalProps,
} from '../reconciliation/Screens/ReconciliationModal'
import {
  clearAndUpdateTransactionFilters,
  clearTransactionFilters,
} from '@/reducers/finances/transactionFiltersReducer'
import {
  Dispatch,
  ReduxState,
  wrapDispatch,
  filterNulls,
} from '@/utils/typeHelpers'
import {
  FinancialAccountWithAdminInfo,
  Reconciliation,
  ReconciliationOrigin,
} from '@/reducers/admin/financialAccountsReducer'
import { getUserById, selectAdminUserState } from '@/selectors/user.selectors'
import { withRouter } from '@/utils/routeHelpers'

import { UserBreadcrumbs } from '../user-record/UserBreadcrumbs'
import {
  getUserFinancialAccounts,
  selectSortedFinancialAccounts,
} from '@/selectors/financeSelectors'
import {
  Alert,
  Text,
  Button as HeardButton,
  Icon,
} from '@/componentsV1/BaseComponents'
import SmartRuleCreationModal from '@/features/admin/smart-rule-creation/SmartRuleCreationModal'
import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import { getFetchError } from '@/reducers/fetch'
import {
  selectBookkeepingReportsById,
  selectBookkeepingReportStatus,
} from '@/selectors/bookkeepingReportsSelectors'
import TransactionCountsCard from './TransactionCountsCard'
import { getSystemCompletedStatuses } from './bookkeepingStatusUtils'
import {
  BookkeepingReport,
  BookkeepingReportStatus,
} from '@/reducers/finances/bookkeepingReportsReducer'
import { selectUserDocumentsByReportId } from '@/features/user/your-practice/documents/lib/userDocuments.selector'
import {
  DATE_FORMATS_LUXON,
  formatISOFromUTC,
  isoToUTCDateTime,
  toUTCDateString,
} from '@/utils/dateHelpers'
import DownloadPayrollReportMenuItem from './DownloadPayrollReportMenuItem'
import { UserWithAdminInfo } from '@/reducers/admin/allUsersReducer'
import { UserAccountCanceledAlert } from '../user-record/UserAccountStates/UserAccountCanceledAlert'
import { UserStateLabel } from '../user-record/UserAccountStates/UserStateLabel'
import { Colors } from '../../../styles/theme'
import { OpenFeature } from '@openfeature/react-sdk'
import { OnboardingAnswers } from '../user-record/OnboardingAnswers'
import AutoReconciliationLoadingIndicator, {
  AutoReconciliationLoadingIndicatorProps,
} from '../reconciliation/Components/AutoReconciliationModal'
import {
  CUBK_AUTORECON_LAUNCH_DATE,
  FIRST_ELIGIBLE_CUBK_REPORTING_PERIOD,
} from '@/constants/autoReconciliationConstants'
import { VirtualStatement } from '../reconciliation/Components/VirtualStatement'
import {
  getVirtualStatement,
  VirtualStatementData,
} from '@/actions/admin/accountReconciliationActions'
import AutoReconciliationRecoveryModal, {
  AutoReconciliationRecoveryModalProps,
} from '../reconciliation/Components/AutoReconciliationRecoveryModal'
import StatementExtractionBadge, {
  isCompletedViaStatementExtraction,
} from '../reconciliation/Components/StatementExtractionBadge'
import { AdminExcludeReportsButton } from '../user-record/AdminConfirmModals'
import { getMissingStatementsForUser } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { FinancialType } from '@/reducers/finances/financialAccountsReducer'

import {
  Badge,
  Separator,
  Text as NewText,
  Breadcrumb as NewBreadcrumb,
  Alert as UIAlert,
} from '@/ui'
import { TypographyStyles } from '@/styles'
import { UserFreeTrialAlert } from '../user-record/UserAccountStates/UserFreeTrialAlert'
const DocumentsTable = ({
  userId,
  report,
}: {
  userId: string
  report?: BookkeepingReport
}) => {
  const accounts = useReselector(getUserFinancialAccounts, userId)
  const user = useReselector(getUserById, userId)
  const statements = useReselector(
    selectUserDocumentsByReportId,
    userId,
    report?.id
  )

  const getMissingStatements = useCallback(
    () => getMissingStatementsForUser(userId),
    [userId]
  )

  const missingStatements = useFetchResponse(getMissingStatements)

  // Check if user and reporting period are eligible for CUBK "No statements expected" logic
  const isEligibleForCUBKLogic = useMemo(() => {
    if (!user || !report) return false

    // Only apply to users who signed up after CUBK launch date
    const userSignupDate = DateTime.fromISO(user.createdAt)
    const userSignedUpAfterCUBK = userSignupDate > CUBK_AUTORECON_LAUNCH_DATE

    // Only apply to months within CUBK scope
    const reportingPeriodEligible =
      report.date >= FIRST_ELIGIBLE_CUBK_REPORTING_PERIOD

    return userSignedUpAfterCUBK && reportingPeriodEligible
  }, [user, report])

  const filteredAccounts = useMemo(() => {
    if (!accounts) return []
    return filterNulls(Object.values(accounts)).filter((account) => {
      if (account.inactive) return false
      return (
        // filter out accounts where a statement exists with that financial account id
        !statements.some((statement) =>
          statement?.statement?.statementFinancialAccounts?.some(
            (statementAccount) =>
              statementAccount.financialAccountId === account.id
          )
        )
      )
    })
  }, [accounts, statements])

  const noStatementExpectedMonths: { [k: string]: boolean } = useMemo(() => {
    return (missingStatements || []).reduce(
      (
        sta,
        { financial_institution_name, name, mask, noStatementExpectedMonths }
      ) => {
        const key = `${financial_institution_name}:${name}:${mask}`

        const matchedStatements = noStatementExpectedMonths.filter(
          (statement) =>
            isoToUTCDateTime(statement).toFormat(
              DATE_FORMATS_LUXON.YEAR_MONTH
            ) === report?.date
        )

        return { ...sta, [key]: Boolean(matchedStatements.length) }
      },
      {}
    )
  }, [missingStatements, report?.date])

  const shouldShowNewAccountText = ({
    plaidInstitutionName,
    name,
    mask,
  }: FinancialAccountWithAdminInfo) => {
    const key = `${plaidInstitutionName}:${name}:${mask}`
    return noStatementExpectedMonths[key]
  }

  const normalize = (str: string) => str.toLowerCase()

  const statementMap: { [key: string]: string[] } = (
    missingStatements || []
  ).reduce((sta, { financial_institution_name, name, mask, statements }) => {
    const key = `${normalize(financial_institution_name || '')}:${normalize(name || '')}:${normalize(mask || '')}`
    return { ...sta, [key]: statements }
  }, {})

  const [statementsNeeded, noStatementNeeded] = useMemo(() => {
    return filteredAccounts.reduce<
      [FinancialAccountWithAdminInfo[], FinancialAccountWithAdminInfo[]]
    >(
      ([needed, notNeeded], account) => {
        if (
          account.accountType === FinancialType.USER_MANUAL_UPLOAD ||
          account.accountType === FinancialType.MANUAL
        ) {
          return [[...needed, account], notNeeded]
        }

        const key = `${normalize(account.plaidInstitutionName || '')}:${normalize(account.name || '')}:${normalize(account.mask || '')}`
        const missingStatements = (statementMap[key] || []).filter(
          (statement) =>
            DateTime.fromISO(statement, { zone: 'utc' }).toFormat(
              DATE_FORMATS_LUXON.YEAR_MONTH
            ) === report?.date
        )

        if (missingStatements.length === 0 && isEligibleForCUBKLogic) {
          return [needed, [...notNeeded, account]]
        }

        return [[...needed, account], notNeeded]
      },
      [[], []]
    )
  }, [filteredAccounts, report?.date, statementMap, isEligibleForCUBKLogic])

  const renderAccountsTable = () => {
    return (
      <>
        {noStatementNeeded.map((account) => {
          return (
            <Table.Row key={account.id}>
              <Table.Cell>
                <Text as="bodyXs">
                  <b> {account.plaidInstitutionName} </b>
                </Text>
                <Text as="bodyXs">
                  {account?.name}:{account?.mask}
                </Text>
              </Table.Cell>
              <Table.Cell>
                <Text as="bodyXs">
                  {shouldShowNewAccountText(account)
                    ? 'New account. Use $0 balances'
                    : 'No statements expected'}
                </Text>
              </Table.Cell>
              <Table.Cell>
                <Text as="bodyXs">-</Text>
              </Table.Cell>
            </Table.Row>
          )
        })}
        {statementsNeeded.map((account) => {
          return (
            <Table.Row
              key={account.id}
              style={{ backgroundColor: Colors.lightOrange }}
            >
              <Table.Cell>
                <Text as="bodyXs">
                  <b> {account.plaidInstitutionName} </b>
                </Text>
                <Text as="bodyXs">
                  {account?.name}:{account?.mask}
                </Text>
              </Table.Cell>
              <Table.Cell>
                {account.statementPermissions === 'plaid_statement' ? (
                  <Text as="bodyXs"> Plaid Statements </Text>
                ) : account.bankAccessEnabledAt ? (
                  <Text as="bodyXs">Limited Bank Access</Text>
                ) : (
                  <Text as="bodyXs"> Manual User Upload </Text>
                )}
              </Table.Cell>
              <Table.Cell>
                <Text as="bodyXs">N/A</Text>
              </Table.Cell>
            </Table.Row>
          )
        })}
      </>
    )
  }
  const renderTable = () => {
    if (statements && statements.length > 0) {
      return statements.map(
        ({ id, signedUrl, fileDescription, createdAt, statement }) => {
          const firstAccount = statement?.statementFinancialAccounts?.[0]
          const institution = firstAccount
            ? accounts?.[firstAccount.financialAccountId]?.plaidInstitutionName
            : null
          const isLBA = firstAccount
            ? accounts?.[firstAccount.financialAccountId]?.bankAccessEnabledAt
            : false
          return (
            <Table.Row key={id}>
              <Table.Cell>
                {institution && (
                  <Text as="bodyXs">
                    <b> {institution} </b>
                  </Text>
                )}
                {accounts &&
                  statement?.statementFinancialAccounts?.map(
                    ({ financialAccountId }) => {
                      return (
                        <Text
                          key={financialAccountId}
                          as="bodyXs"
                          style={{ marginBottom: 6 }}
                        >
                          {accounts[financialAccountId]?.name}:
                          {accounts[financialAccountId]?.mask}
                        </Text>
                      )
                    }
                  )}
              </Table.Cell>
              <Table.Cell>
                <a
                  href={signedUrl || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    overflowWrap: 'anywhere',
                    fontSize: 12,
                  }}
                >
                  {isLBA ? 'Limited Bank Access' : fileDescription}
                </a>
              </Table.Cell>
              <Table.Cell>
                {DateTime.fromISO(createdAt).toFormat(
                  DATE_FORMATS_LUXON.DISPLAY_SHORT
                )}
              </Table.Cell>
            </Table.Row>
          )
        }
      )
    }

    if (noStatementNeeded.length === 0 && statementsNeeded.length === 0) {
      return (
        <Table.Row>
          <Table.Cell colSpan="3" textAlign="center">
            <Header as="h6">No Documents</Header>
          </Table.Cell>
        </Table.Row>
      )
    }
    return null
  }
  return (
    <Table striped compact="very">
      {statements && statements.length > 0 && (
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell width={4}>Account</Table.HeaderCell>
            <Table.HeaderCell width={4}>File Name</Table.HeaderCell>
            <Table.HeaderCell width={2}>Uploaded</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
      )}
      <Table.Body>
        {renderTable()}
        {renderAccountsTable()}
      </Table.Body>
    </Table>
  )
}

const StatementCard = ({
  report,
  userId,
}: {
  report?: BookkeepingReport
  userId: string
}) => {
  return (
    <Card fluid>
      <Card.Content>
        <Header as="h5">Statements</Header>
        <DocumentsTable userId={userId} report={report} />
      </Card.Content>
    </Card>
  )
}

const TIME_FORMAT = 'h:mm:ss a'

const statuses = (
  currentRecon?: Reconciliation,
  count?: number,
  retryWindowEndsAt?: string | null,
  blockedOnMonth?: string | null
) => {
  const date = currentRecon?.updatedAt
  // The period's opening, not the row's last write: `updatedAt` below is what the
  // "last edited" popup wants, and reads as a period date in this subtitle.
  const openedOn = currentRecon?.startingBalanceDate
  const subtitleDate = openedOn
    ? toUTCDateString({
        value: openedOn,
        format: DATE_FORMATS_LUXON.DISPLAY_LONG,
      })
    : ''
  const popupDate = date
    ? toUTCDateString({ value: date, format: DATE_FORMATS_LUXON.DISPLAY_SHORT })
    : ''
  const popupTime = date
    ? toUTCDateString({ value: date, format: TIME_FORMAT })
    : ''

  const retryWindowEnd = retryWindowEndsAt
    ? formatISOFromUTC(retryWindowEndsAt, DATE_FORMATS_LUXON.DISPLAY_SHORT)
    : ''

  const blockedOnMonthLabel = blockedOnMonth
    ? DateTime.fromFormat(blockedOnMonth, DATE_FORMATS_LUXON.YEAR_MONTH, {
        zone: 'utc',
      }).toFormat(DATE_FORMATS_LUXON.MONTH_YEAR)
    : null

  return {
    complete: {
      color: 'green' as const,
      label: 'complete',
      subtitle: null,
      type: 'complete',
      currentRecon,
      popupText: null,
    },
    incomplete: {
      color: 'red' as const,
      label: 'incomplete',
      subtitle: `Resume from ${subtitleDate}`,
      type: 'incomplete',
      popupText: `Last edited on ${popupDate} at ${popupTime} UTC`,
      currentRecon,
    },
    blocked: {
      color: 'red' as const,
      label: 'blocked',
      subtitle: blockedOnMonthLabel
        ? `Blocked: Reconcile ${blockedOnMonthLabel} first`
        : 'Blocked: an earlier month must be reconciled first',
      type: 'blocked',
      popupText: `Last attempted on ${popupDate} at ${popupTime} UTC`,
      currentRecon,
    },
    // derived - - -
    not_reconciled: {
      color: 'yellow' as const,
      label: 'not reconciled',
      subtitle: 'This acct has not been reconciled this month',
      type: 'not_reconciled',
      popupText: "Click 'Reconcile' to reconcile this month",
      currentRecon,
    },
    needs_updating: {
      color: 'yellow' as const,
      label: 'needs updating',
      type: 'needs_updating',
      // The count is of transactions dated *within* this reconciliation's window that were
      // added or changed after it was reconciled, so the settled totals may be stale.
      subtitle: `There are ${count || 0} new or changed transaction(s) within this period`,
      popupText: `${
        count || 0
      } transaction(s) within this period were added or changed since it was reconciled on ${popupDate} at ${popupTime} UTC, so this reconciliation may be out of date.`,
      currentRecon,
    },
    never_reconciled: {
      color: 'red' as const,
      label: 'never reconciled',
      subtitle: 'This acct has never been reconciled',
      type: 'never_reconciled',
      popupText: "Click 'Reconcile' to begin your first reconciliation",
      currentRecon,
    },
    is_retrying: {
      color: 'red' as const,
      label: 'retrying auto-reconciliation',
      subtitle: retryWindowEnd
        ? `Manual reconciliation is disabled until ${retryWindowEnd}`
        : 'Manual reconciliation is disabled while auto-reconciliation retries',
      type: 'is_retrying',
      popupText: `Auto-reconciliation is currently retrying. Last attempted on ${popupDate} at ${popupTime} UTC`,
      currentRecon,
    },
    // - - - end derived
  }
}

/**
 * The card for a period. The staleness count arrives from its own endpoint, so
 * `needs_updating` is layered on here.
 */
export const getAccountStatus = (
  period: PeriodState,
  currentRecon: Reconciliation | null,
  staleTransactionCount: number | undefined
) => {
  const all = statuses(
    currentRecon ?? undefined,
    staleTransactionCount,
    period.retryWindowEndsAt,
    period.blockedOnMonth
  )

  if (period.status === 'never_reconciled') return all.never_reconciled
  if (period.status === 'not_reconciled') return all.not_reconciled
  if (period.status === 'retrying') return all.is_retrying
  if (period.status === 'blocked') return all.blocked
  if (period.status === 'incomplete') return all.incomplete

  return (staleTransactionCount ?? 0) > 0 ? all.needs_updating : all.complete
}

const ReconciliationText = ({
  text,
  date,
  color,
  style = {},
}: {
  text: string
  date?: string | null
  color?: 'green' | 'red'
  style?: CSSProperties
}) => (
  <div style={style}>
    <Text color={color} style={{ fontSize: 14, fontWeight: 500 }}>
      {text}
    </Text>
    {date && (
      <Text as="bodyXs" color={color}>
        {formatISOFromUTC(date, DATE_FORMATS_LUXON.DISPLAY_SHORT)}
      </Text>
    )}
  </div>
)

const ViewVirtualStatement = ({
  reconciliationId,
  style = {},
  toggleVirtualStatement,
}: {
  reconciliationId: number
  style?: CSSProperties
  toggleVirtualStatement: (reconciliationId?: number) => void
}) => (
  <HeardButton
    variant="secondaryLink"
    style={{ color: Colors.black, fontSize: 12, ...style }}
    onClick={() => toggleVirtualStatement(reconciliationId)}
  >
    View virtual bank statement
  </HeardButton>
)

type ReconcileMode = 'auto' | 'manual' | 'recover'

const RECONCILE_ACTION_TEXT: Record<ReconcileMode, string> = {
  auto: 'Auto Reconcile',
  manual: 'Manually Reconcile',
  recover: 'Recover Reconciliation',
}

const ReconciliationAction = ({
  status,
  onAction,
  toggleVirtualStatement,
  disabled,
  actionText,
  secondaryAction,
  isRetrying = false,
}: {
  status: ReturnType<typeof getAccountStatus>
  onAction: () => void
  toggleVirtualStatement: (reconciliationId?: number) => void
  disabled: boolean
  actionText: string
  secondaryAction?: { text: string; onClick: () => void }
  isRetrying: boolean
}) => {
  const currentRecon = status.currentRecon

  return (
    <Card.Content style={{ margin: '0 auto' }}>
      {status.type === 'complete' && (
        <Grid>
          <Grid.Row>
            <Grid.Column width={2}>
              <Icon icon={faCircleCheck} color="accentGreen" size="xl" />
            </Grid.Column>
            <Grid.Column width={14}>
              {currentRecon &&
              isCompletedViaStatementExtraction(currentRecon) ? (
                <div className="pr-10">
                  <StatementExtractionBadge />
                  {currentRecon.createdAt && (
                    <Text as="bodyXs" color="green" className="pt-1">
                      {formatISOFromUTC(
                        currentRecon.createdAt,
                        DATE_FORMATS_LUXON.DISPLAY_SHORT
                      )}
                    </Text>
                  )}
                  <ViewVirtualStatement
                    reconciliationId={currentRecon.id}
                    toggleVirtualStatement={toggleVirtualStatement}
                  />
                </div>
              ) : (
                <>
                  {currentRecon?.origin === ReconciliationOrigin.autoRecon && (
                    <>
                      <ReconciliationText
                        text="Auto-reconciled"
                        date={
                          currentRecon.reconCompletedAt ??
                          currentRecon.createdAt
                        }
                        color="green"
                      />
                      <ViewVirtualStatement
                        reconciliationId={currentRecon.id}
                        toggleVirtualStatement={toggleVirtualStatement}
                      />
                    </>
                  )}
                  {currentRecon &&
                    currentRecon.origin !== ReconciliationOrigin.autoRecon && (
                      <ReconciliationText
                        text="Manually Reconciled"
                        date={
                          currentRecon.reconCompletedAt ??
                          currentRecon.createdAt
                        }
                        color="green"
                        style={{ paddingRight: 40 }}
                      />
                    )}
                </>
              )}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={16}>
              {/* Recover's refusal tells bookkeepers to redo a settled month; without
                  this the advice dead-ends. Disabled until the books are re-started. */}
              <Button color="blue" onClick={onAction} disabled={disabled}>
                {actionText}
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      )}
      {status.type !== 'complete' && (
        <>
          {isCompletedViaStatementExtraction(currentRecon) && (
            <div className="pb-2">
              <StatementExtractionBadge />
            </div>
          )}
          {currentRecon?.origin === ReconciliationOrigin.autoRecon &&
            currentRecon.status === 'complete' && (
              <>
                <Icon
                  icon={faCircleCheck}
                  color="accentGreen"
                  size="xl"
                  style={{ display: 'inline-block', paddingRight: 5 }}
                />
                <ReconciliationText
                  text="Auto-reconciled"
                  color="green"
                  style={{ display: 'inline-block', paddingBottom: 5 }}
                />
              </>
            )}
          {currentRecon?.origin === ReconciliationOrigin.autoRecon &&
            currentRecon.status === 'incomplete' &&
            !isRetrying && (
              <>
                <Icon
                  icon={faCircleXmark}
                  color="red"
                  size="xl"
                  style={{ display: 'inline-block', paddingRight: 5 }}
                />
                <ReconciliationText
                  text="Auto-reconciliation failed"
                  color="red"
                  style={{ display: 'inline-block', paddingBottom: 5 }}
                />
              </>
            )}
          <Button
            color="blue"
            onClick={onAction}
            fluid={
              currentRecon?.origin === ReconciliationOrigin.autoRecon ||
              Boolean(secondaryAction)
            }
            disabled={disabled}
          >
            {actionText}
          </Button>
          {secondaryAction && (
            <div className="pt-1">
              <HeardButton
                variant="secondary"
                fullWidth
                onClick={secondaryAction.onClick}
              >
                {secondaryAction.text}
              </HeardButton>
            </div>
          )}
          {/* Recover writes the month it blocks as `bookkeeper`, so gating on origin alone
              hides the only route to the panel naming the month it waits on. */}
          {currentRecon &&
            (currentRecon.origin === ReconciliationOrigin.autoRecon ||
              status.type === 'blocked') && (
              <ViewVirtualStatement
                reconciliationId={currentRecon.id}
                style={{ paddingTop: 5 }}
                toggleVirtualStatement={toggleVirtualStatement}
              />
            )}
        </>
      )}
    </Card.Content>
  )
}

/** Mirrors the API's `isReconciliationLocked`; `reOpenBooks` clears both. */
const isReconciliationLocked = (report: BookkeepingReport | undefined) =>
  Boolean(report?.reconciledAt ?? report?.completedAt)

/**
 * Reasons worth explaining on a card that still has an action. The structural ones (a
 * manual account, no qualifying reconciliation) would notice-spam healthy cards.
 */
const AUTO_REASONS_WORTH_EXPLAINING: ReconciliationUnavailableReason[] = [
  ReconciliationUnavailableReason.ImportedTransactions,
]

/**
 * An account the payload has not declared. A request still outstanding reads as pending;
 * one that came back without an answer reads as an error and offers the way out.
 */
const BLANK_CAPABILITIES = {
  unavailableMessage: {},
  accountFeed: {
    isDisconnected: false,
    isQuiet: false,
    isUnreliable: false,
    lastTransactionDate: null,
  },
  period: {
    reconciliationId: null,
    status: 'not_reconciled',
    blockedOnMonth: null,
    retryWindowEndsAt: null,
  },
} satisfies Pick<
  ReconciliationCapabilities,
  'unavailableMessage' | 'accountFeed' | 'period'
>

const UNDECLARED_ACTION = {
  pending: { label: 'loading', actionText: 'Loading', reason: undefined },
  unavailable: {
    label: 'unavailable',
    actionText: 'Unavailable',
    reason: 'Available actions could not be loaded. Refresh to try again.',
  },
} as const

const AccountSumCards = ({
  userId,
  financialAccounts,
  newTransactionCounts,
  capabilitiesByAccount,
  capabilitiesInFlight,
  onReconcileClick,
  toggleVirtualStatement,
}: {
  userId: string
  financialAccounts: FinancialAccountWithAdminInfo[]
  newTransactionCounts: { [key: number]: number }
  capabilitiesByAccount: { [accountId: number]: ReconciliationCapabilities }
  capabilitiesInFlight: boolean
  onReconcileClick: (
    formType: string,
    account: FinancialAccountWithAdminInfo,
    reconType: ReconcileMode
  ) => void
  toggleVirtualStatement: (reconciliationId?: number) => void
}) => {
  const user = useReselector(getUserById, userId)

  useEffect(() => {
    if (!user) return
    const currentContext = OpenFeature.getContext()
    // only set context if current context already exists
    if (!currentContext || Object.keys(currentContext).length === 0) return
    // Re-setting an unchanged context still reconciles the provider, which re-evaluates
    // every flag on the page.
    if (currentContext.reconUserEmail === user.email) return
    // make sure current context is included in new context
    OpenFeature.setContext({
      ...currentContext,
      reconUserEmail: user.email,
    })
  }, [user])

  const style = {
    card: { position: 'relative' },
    inactive: { opacity: '0.5' },
    label: { margin: '2px' },
    subtitle: { textAlign: 'center' as const, textTransform: 'none' as const },
  }

  return (
    <Card.Group itemsPerRow={4}>
      {financialAccounts.map((account) => {
        const capabilities = capabilitiesByAccount[account.id]
        const undeclared = capabilities
          ? undefined
          : UNDECLARED_ACTION[capabilitiesInFlight ? 'pending' : 'unavailable']
        const { accountFeed, period, unavailableMessage } =
          capabilities ?? BLANK_CAPABILITIES

        // Looked up by the id the server resolved, so the panel and the write path
        // cannot disagree about which row the month owns.
        const currentRecon =
          account.reconciliations.find(
            (recon) => recon.id === period.reconciliationId
          ) ?? null

        const isRetrying = period.status === 'retrying'
        const status = getAccountStatus(
          period,
          currentRecon,
          newTransactionCounts[account.id]
        )

        const dateOfLastTransaction = accountFeed.lastTransactionDate

        const formattedDateOfLastTransaction =
          dateOfLastTransaction &&
          isoToUTCDateTime(dateOfLastTransaction).toFormat(
            DATE_FORMATS_LUXON.DISPLAY_SHORT
          )

        // Recover reopens the predecessor auto-recon would otherwise chain from, so it
        // sits alongside manual reconciliation rather than in place of it.
        const primaryMode: ReconcileMode = capabilities?.canRecover
          ? 'manual'
          : capabilities?.canAutoRecon
            ? 'auto'
            : 'manual'
        const secondaryMode: ReconcileMode | null = capabilities?.canRecover
          ? 'recover'
          : null

        const noActionAvailable =
          !capabilities?.canAutoRecon &&
          !capabilities?.canManual &&
          !capabilities?.canRecover

        // An open period is the one place recover would otherwise have been offered, so
        // withholding it there says why rather than dropping the button silently.
        const withheldRecover =
          period.status === 'incomplete'
            ? unavailableMessage.recover
            : undefined

        const worthExplainingAuto =
          capabilities?.unavailableReason.autoRecon &&
          AUTO_REASONS_WORTH_EXPLAINING.includes(
            capabilities.unavailableReason.autoRecon
          )
            ? unavailableMessage.autoRecon
            : undefined

        // A disabled control always says why; an enabled one explains only the
        // withheld action a bookkeeper would otherwise have expected.
        const unavailableReasonText = undeclared
          ? undeclared.reason
          : noActionAvailable
            ? (unavailableMessage.manual ??
              unavailableMessage.recover ??
              unavailableMessage.autoRecon)
            : (withheldRecover ?? worthExplainingAuto)

        return (
          <Card
            style={
              account.inactive
                ? style.inactive
                : {
                    backgroundColor: accountFeed.isUnreliable
                      ? Colors.lightRed
                      : Colors.white,
                  }
            }
            fluid
            key={account.id}
          >
            <Card.Content>
              {account.inactive && (
                <Label style={style.label} color="orange">
                  inactive account
                </Label>
              )}
              {account.statementPermissions === 'plaid_statement' ? (
                <Label style={style.label} color="blue">
                  Plaid Statement
                </Label>
              ) : account.bankAccessEnabledAt ? (
                <Label style={style.label} color="violet">
                  LBA
                </Label>
              ) : null}
              <Label
                style={style.label}
                color={account.accountType === 'plaid' ? 'green' : 'red'}
              >
                {account.accountType}
              </Label>
              {accountFeed.isDisconnected && (
                <Label style={style.label} color="red">
                  Disconnected
                </Label>
              )}
              <Label
                style={style.label}
                basic={!undeclared && status.type === 'never_reconciled'}
                color={undeclared ? 'grey' : status.color}
              >
                {undeclared?.label ?? status.label}
              </Label>
              {account.inactive && (
                <Label style={style.label} color="brown">
                  {account.excludeFromReports
                    ? 'excluded from reports'
                    : 'included in reports'}
                </Label>
              )}
            </Card.Content>
            <Card.Content>
              <Card.Header>
                <Header as="h4" style={{ paddingLeft: 0 }}>
                  {account.plaidInstitutionName}
                </Header>
              </Card.Header>
              <Card.Description>
                <p>
                  <b>{account.name}:</b> {account.mask}
                </p>
                <p>
                  {account.type}&nbsp;—&nbsp;{account.subtype}
                </p>
              </Card.Description>
            </Card.Content>
            {(accountFeed.isDisconnected || accountFeed.isQuiet) && (
              <Card.Content extra>
                <Text as="bodyXs" color="red">
                  <b>Error: </b>
                  {accountFeed.isDisconnected
                    ? 'Disconnected'
                    : 'Over 7 days since last transaction'}
                </Text>
                <Text as="bodyXs" color="red">
                  <b>Date of last transaction:</b>{' '}
                  {formattedDateOfLastTransaction}
                </Text>
              </Card.Content>
            )}
            {unavailableReasonText && (
              <Card.Content extra>
                <Text as="bodySm">{unavailableReasonText}</Text>
              </Card.Content>
            )}
            {!undeclared &&
              !accountFeed.isUnreliable &&
              status.popupText &&
              status.subtitle && (
                <Card.Content extra>
                  <Popup
                    inverted
                    disabled={account.inactive}
                    content={`id ${account.id}: ${status.popupText}`}
                    trigger={
                      <p color={status.color} style={style.subtitle}>
                        {status.subtitle}
                      </p>
                    }
                  />
                </Card.Content>
              )}

            {!account.inactive && (
              <ReconciliationAction
                status={status}
                actionText={
                  undeclared?.actionText ?? RECONCILE_ACTION_TEXT[primaryMode]
                }
                onAction={() => {
                  // Passing "status.type" instead of "reconciliation.status"
                  // b/c reconciliation.status does not include derived types [needs_updating, never_reconciled]
                  onReconcileClick(status.type, account, primaryMode)
                }}
                secondaryAction={
                  secondaryMode
                    ? {
                        text: RECONCILE_ACTION_TEXT[secondaryMode],
                        onClick: () =>
                          onReconcileClick(status.type, account, secondaryMode),
                      }
                    : undefined
                }
                toggleVirtualStatement={toggleVirtualStatement}
                isRetrying={isRetrying}
                disabled={
                  primaryMode === 'auto'
                    ? !capabilities?.canAutoRecon
                    : !capabilities?.canManual
                }
              />
            )}
            {account.inactive && (
              <AdminExcludeReportsButton
                userId={parseInt(userId)}
                account={account}
              />
            )}
          </Card>
        )
      })}
    </Card.Group>
  )
}

export const getReportTransactionQueryProps = (
  userId: number,
  report: BookkeepingReport | undefined
) => {
  const startDate = report
    ? DateTime.fromISO(report.startDate, { zone: 'utc' })
        .startOf('month')
        .toFormat(DATE_FORMATS_LUXON.INPUT)
    : undefined
  const endDate = report
    ? DateTime.fromISO(report.endDate, { zone: 'utc' })
        .endOf('month')
        .toFormat(DATE_FORMATS_LUXON.INPUT)
    : undefined
  return {
    userId,
    startDate,
    endDate,
  }
}

type ParentProps = {
  params: { userId: string; reportId: string }
}
type DispatchProps = ReturnType<typeof mapDispatchToProps>
type StateProps = ReturnType<typeof mapStateToProps>

type Props = ParentProps & DispatchProps & StateProps

interface State {
  loading: boolean
  inProgressOpen: boolean
  completeOpen: boolean
  reconciledOpen: boolean
  doneOpen: boolean
  smartRuleOpen: boolean
  reconciliationModalProps: ReconciliationModalProps | null
  newTransactionCounts: { [key: number]: number }
  unreconciledTransactionCounts: { [key: number]: number }
  declaredCapabilities: {
    reportId: number
    byAccount: { [accountId: number]: ReconciliationCapabilities }
  } | null
  capabilitiesInFlight: boolean
  autoReconciliationLoadingIndicatorProps: AutoReconciliationLoadingIndicatorProps | null
  virtualStatementData: VirtualStatementData | null
  autoReconciliationRecoveryModalProps: AutoReconciliationRecoveryModalProps | null
  transactionChangeCount: number
}

const UserRecordHeader = ({ user }: { user?: UserWithAdminInfo }) => {
  const userState = useReselector(selectAdminUserState, user?.id)
  if (!userState) {
    return null
  }

  return (
    <div>
      <Header as="h3" {...(userState === 'unpaid' && { color: 'red' })}>
        <UserStateLabel user={user} />
        <UserAccountCanceledAlert user={user} />
      </Header>
    </div>
  )
}

export class MonthlyBooksPanel extends Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = {
      loading: true,
      inProgressOpen: false,
      completeOpen: false,
      reconciledOpen: false,
      doneOpen: false,
      smartRuleOpen: false,
      reconciliationModalProps: null,
      newTransactionCounts: {},
      unreconciledTransactionCounts: {},
      declaredCapabilities: null,
      capabilitiesInFlight: true,
      autoReconciliationLoadingIndicatorProps: null,
      virtualStatementData: null,
      autoReconciliationRecoveryModalProps: null,
      transactionChangeCount: 0,
    }
  }

  async componentDidMount() {
    const {
      params: { userId },
      report,
    } = this.props

    await Promise.all([
      this.refreshTransactionCounts(),
      this.props.adminFetchSingleUser(userId),
      this.props.clearAndUpdateTransactionFilters(
        getReportTransactionQueryProps(Number(userId), report)
      ),
      this.props.fetchAdminUsersBookkeepingReports(userId),
      this.props.fetchAdminUsersDocuments(userId),
      this.props.fetchUserFinancialAccounts(userId),
    ])

    this.setState({ loading: false })
  }

  componentDidUpdate(prevProps: Props) {
    const {
      params: { userId },
      report,
    } = this.props

    if (report && report?.id !== prevProps.report?.id) {
      this.props.clearAndUpdateTransactionFilters(
        getReportTransactionQueryProps(Number(userId), report)
      )
      // The staleness count is scoped to the new report's reconciliation window.
      void this.refreshTransactionCounts()
      return
    }

    // The status endpoint replaces the stored report in place, so the id does not move.
    // Without this, Re-start Books leaves every action greyed until a reload.
    const lockChanged =
      report?.reconciledAt !== prevProps.report?.reconciledAt ||
      report?.completedAt !== prevProps.report?.completedAt

    if (report && lockChanged) {
      void this.refreshTransactionCounts()
    }
  }

  componentWillUnmount() {
    this.props.clearTransactionFilters()
  }

  handleBooksUpdate = async (status: BookkeepingReportStatus) => {
    const {
      params: { userId, reportId },
    } = this.props

    if (!reportId) {
      return
    }
    const updated = await this.props.updateUsersBookkeepingReportStatus(
      reportId,
      {
        status,
        bookkeepingReportId: reportId,
      }
    )
    // On failure (e.g. the reconciled-before-done guard rejects) the error is
    // surfaced via getFetchError in the Alert below and the action returns
    // undefined; refetch so the panel reflects the true status and the Done
    // control re-disables.
    if (!updated) {
      await this.props.fetchAdminUsersBookkeepingReports(userId)
    }
  }

  toggleVirtualStatement = async (reconciliationId?: number) => {
    if (this.state.virtualStatementData || !reconciliationId) {
      this.setState({ virtualStatementData: null })
      return
    }
    const virtualStatementData =
      await this.props.getVirtualStatement(reconciliationId)

    if (virtualStatementData) {
      this.setState({ virtualStatementData })
    } else {
      alert(`Error fetching virtual statement for ${reconciliationId}`)
    }
  }

  // The staleness count is scoped to the reconciliation the viewed report owns, so it must
  // be refetched whenever the report changes or a reconciliation is written.
  refreshTransactionCounts = async () => {
    const {
      params: { userId },
      report,
    } = this.props
    const requestedReportId = report?.id
    if (requestedReportId === undefined) return undefined

    this.setState({ capabilitiesInFlight: true })

    const [
      newTransactionCounts,
      unreconciledTransactionCounts,
      reconciliationCapabilities,
    ] = await Promise.all([
      this.props.fetchStaleTransactionCountsForReport(
        userId,
        requestedReportId
      ),
      this.props.fetchTransactionCountsSinceLastReconciliation(userId),
      this.props.fetchReconciliationCapabilities(userId, requestedReportId),
    ])
    // A newer report may have been selected while this request was in flight; its counts
    // describe a different reconciliation, so drop this stale response rather than let it
    // overwrite the current report's state.
    if (this.props.report?.id !== requestedReportId) return undefined

    if (newTransactionCounts) {
      this.setState({ newTransactionCounts })
    }
    if (unreconciledTransactionCounts) {
      this.setState({ unreconciledTransactionCounts })
    }
    if (reconciliationCapabilities) {
      this.setState({
        declaredCapabilities: {
          reportId: requestedReportId,
          byAccount: reconciliationCapabilities,
        },
      })
    }
    // Cleared whether or not an answer came back: an undeclared card is only pending
    // while a request is outstanding.
    this.setState({ capabilitiesInFlight: false })

    return reconciliationCapabilities
  }

  /**
   * Re-read after a write. The declaration names the row the write just created, so the
   * account records have to come with it, forced past their per-user cache: a card that
   * cannot find the declared id shows a settled period with none of its detail.
   */
  refreshAfterReconciliationWrite = async () => {
    const [, capabilities] = await Promise.all([
      this.props.fetchUserFinancialAccounts(this.props.params.userId, true),
      this.refreshTransactionCounts(),
    ])

    return capabilities
  }

  renderProgressButton(systemCompleted: Set<BookkeepingReportStatus>) {
    const { status, report } = this.props
    const showBolt = systemCompleted.has(BookkeepingReportStatus.in_progress)

    if (
      isReconciliationLocked(report) ||
      status === BookkeepingReportStatus.done
    ) {
      return (
        <Button
          size="mini"
          className="headerButton"
          secondary
          disabled={false}
          onClick={() => this.setState({ inProgressOpen: true })}
        >
          {showBolt && <Icon icon={faBolt} />} 1. Re-start Books
        </Button>
      )
    } else if (
      status === BookkeepingReportStatus.plaid_error ||
      status === BookkeepingReportStatus.missing_info
    ) {
      return (
        <Button
          size="mini"
          className="headerButton"
          secondary
          disabled={false}
          onClick={() =>
            this.handleBooksUpdate(BookkeepingReportStatus.in_progress)
          }
        >
          {showBolt && <Icon icon={faBolt} />} 1. Books Started
        </Button>
      )
    }

    return (
      <Button
        size="mini"
        className="headerButton"
        secondary
        disabled={status !== 'not_started'}
        onClick={() => this.setState({ inProgressOpen: true })}
      >
        {showBolt && <Icon icon={faBolt} />} 1. Books Started
      </Button>
    )
  }
  renderCompletedButton(systemCompleted: Set<BookkeepingReportStatus>) {
    const { status } = this.props
    const showBolt = systemCompleted.has(BookkeepingReportStatus.categorized)

    if (status !== BookkeepingReportStatus.in_progress) {
      return (
        <Button size="mini" className="headerButton" secondary disabled>
          {showBolt && <Icon icon={faBolt} />} 2. Categorization Complete
        </Button>
      )
    }
    return (
      <Button
        size="mini"
        className="headerButton"
        secondary
        onClick={() => this.setState({ completeOpen: true })}
      >
        {showBolt && <Icon icon={faBolt} />} 2. Categorization Complete
      </Button>
    )
  }

  renderActionsBar() {
    const {
      inProgressOpen,
      completeOpen,
      reconciledOpen,
      doneOpen,
      smartRuleOpen,
    } = this.state
    const { report, status, user, updateStatusError } = this.props
    const systemCompleted = getSystemCompletedStatuses(report?.statuses ?? [])

    const completeBooksDisabled =
      !report?.reconciledAt ||
      status === BookkeepingReportStatus.in_progress ||
      status === BookkeepingReportStatus.done
    return (
      <>
        {updateStatusError && (
          <UIAlert
            variant="error"
            description={updateStatusError.message}
            style={{ marginBottom: 10 }}
          />
        )}
        <Menu secondary size="mini">
          <Menu.Menu position="right">
            <Menu.Item>
              {this.renderProgressButton(systemCompleted)}
              <Modal
                size="mini"
                content="Are you sure you want to start these books?"
                open={inProgressOpen}
                onCancel={() => this.setState({ inProgressOpen: false })}
                onConfirm={() => {
                  this.handleBooksUpdate(BookkeepingReportStatus.in_progress)
                  this.setState({ inProgressOpen: false })
                }}
              >
                <Modal.Header>
                  <Header>Books Started</Header>
                </Modal.Header>
                <Modal.Content>
                  <Text>Are you sure you want to start these books?</Text>
                </Modal.Content>
                <Modal.Actions>
                  <Button
                    onClick={() => this.setState({ inProgressOpen: false })}
                    variant="primary"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      this.handleBooksUpdate(
                        BookkeepingReportStatus.in_progress
                      )
                      this.setState({ inProgressOpen: false })
                    }}
                  >
                    Confirm Start
                  </Button>
                </Modal.Actions>
              </Modal>
            </Menu.Item>
            <Menu.Item>
              {this.renderCompletedButton(systemCompleted)}
              <Modal
                size="mini"
                open={completeOpen}
                onCancel={() => this.setState({ completeOpen: false })}
                onConfirm={() => {
                  this.handleBooksUpdate(BookkeepingReportStatus.categorized)
                  this.setState({ smartRuleOpen: true, completeOpen: false })
                }}
              >
                <Modal.Header>
                  <Header>Categorization Complete</Header>
                </Modal.Header>
                <Modal.Content>
                  <Text>Are you sure you want to complete these books?</Text>
                </Modal.Content>
                <Modal.Actions>
                  <Button
                    onClick={() => this.setState({ completeOpen: false })}
                    variant="primary"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      this.handleBooksUpdate(
                        BookkeepingReportStatus.categorized
                      )
                      this.setState({
                        smartRuleOpen: true,
                        completeOpen: false,
                      })
                    }}
                  >
                    Confirm Completion
                  </Button>
                </Modal.Actions>
              </Modal>
              {user && (
                <SmartRuleCreationModal
                  open={smartRuleOpen}
                  close={() => this.setState({ smartRuleOpen: false })}
                  user={user}
                />
              )}
            </Menu.Item>

            <Menu.Item>
              <Button
                size="mini"
                className="headerButton"
                secondary
                disabled={status !== 'categorized'}
                onClick={() => this.setState({ reconciledOpen: true })}
              >
                {systemCompleted.has(BookkeepingReportStatus.reconciled) && (
                  <Icon icon={faBolt} />
                )}{' '}
                3. Reconciliation Complete
              </Button>

              <Modal
                size="mini"
                open={reconciledOpen}
                onCancel={() => this.setState({ reconciledOpen: false })}
                onConfirm={() => {
                  this.handleBooksUpdate(BookkeepingReportStatus.reconciled)
                  this.setState({ reconciledOpen: false })
                }}
              >
                <Modal.Header>
                  <Header>Reconciliation Complete</Header>
                </Modal.Header>
                <Modal.Content>
                  <Text>Are you sure you want to reconcile these books?</Text>
                </Modal.Content>
                <Modal.Actions>
                  <Button
                    onClick={() => this.setState({ reconciledOpen: false })}
                    variant="primary"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      this.handleBooksUpdate(BookkeepingReportStatus.reconciled)
                      this.setState({ reconciledOpen: false })
                    }}
                  >
                    Confirm Reconciliation
                  </Button>
                </Modal.Actions>
              </Modal>
            </Menu.Item>

            <Menu.Item>
              <Button
                size="mini"
                className="headerButton"
                secondary
                disabled={completeBooksDisabled}
                onClick={() => this.setState({ doneOpen: true })}
              >
                {systemCompleted.has(BookkeepingReportStatus.done) && (
                  <Icon icon={faBolt} />
                )}{' '}
                4. Done
              </Button>
              <Modal
                size="mini"
                open={doneOpen}
                onCancel={() => this.setState({ doneOpen: false })}
                onConfirm={() => {
                  this.handleBooksUpdate(BookkeepingReportStatus.done)
                  this.setState({ doneOpen: false })
                }}
              >
                <Modal.Header>
                  <Header>Done</Header>
                </Modal.Header>
                <Modal.Content>
                  <Text>
                    Are you sure you want to set report as DONE? The user will
                    see this on their Monthly Bookkeeping email.
                  </Text>
                </Modal.Content>
                <Modal.Actions>
                  <Button
                    onClick={() => this.setState({ doneOpen: false })}
                    variant="primary"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      this.handleBooksUpdate(BookkeepingReportStatus.done)
                      this.setState({ doneOpen: false })
                    }}
                  >
                    Confirm Done
                  </Button>
                </Modal.Actions>
              </Modal>
            </Menu.Item>
          </Menu.Menu>
        </Menu>
      </>
    )
  }

  renderBooks() {
    const {
      newTransactionCounts,
      unreconciledTransactionCounts,
      declaredCapabilities,
      capabilitiesInFlight,
      loading,
      reconciliationModalProps,
      autoReconciliationLoadingIndicatorProps,
      autoReconciliationRecoveryModalProps,
      transactionChangeCount,
    } = this.state
    const {
      user,
      params: { userId, reportId },
      report,
      accounts,
      status,
    } = this.props

    // A declaration answers for one report, so while a newly selected report's is in
    // flight nothing is declared and every account withholds its actions.
    const capabilitiesByAccount: {
      [accountId: number]: ReconciliationCapabilities
    } =
      declaredCapabilities && declaredCapabilities.reportId === report?.id
        ? declaredCapabilities.byAccount
        : {}

    const bookkeepingComplete = status === BookkeepingReportStatus.done
    // Drives the banner and Re-start Books only. Whether an account can be reconciled is
    // declared per account; this is the report's own state.
    const reconcileLocked =
      isReconciliationLocked(report) || bookkeepingComplete
    const lockedAt = report?.completedAt ?? report?.reconciledAt

    // When a transaction is changed, we need to increment the transaction change count
    // so that we can update the transaction counts card
    const onTransactionChange = () =>
      this.setState({ transactionChangeCount: transactionChangeCount + 1 })

    const onRecoverModalClose = async (
      reconciliation: Reconciliation | null
    ) => {
      this.setState({ autoReconciliationRecoveryModalProps: null })
      if (reconciliation) {
        const virtualStatementData = await this.props.getVirtualStatement(
          reconciliation.id
        )
        if (virtualStatementData) {
          this.setState({ virtualStatementData })
        }
      }
      await this.refreshAfterReconciliationWrite()
    }

    const onReconcileClick = (
      formType: string,
      account: FinancialAccountWithAdminInfo,
      reconType: ReconcileMode
    ) => {
      // Prevent reconciliation for a "never_reconciled" account which does not have any
      // transactions yet. Not the staleness count: that one is 0 for every never-reconciled
      // account, so reading it here would block every first reconciliation.
      if (formType === 'never_reconciled') {
        const count = unreconciledTransactionCounts[account.id]

        if (count === 0) {
          alert(
            'This account does not have any transactions yet. Please force pull, or retry reconciling at a later time'
          )
          return
        }
      }

      if (!user || !report) {
        return
      }

      const onRecoverModalManualReconcile = () => {
        this.setState({ autoReconciliationRecoveryModalProps: null })
        onReconcileClick(formType, account, 'manual')
      }

      if (reconType === 'auto') {
        this.setState({
          autoReconciliationLoadingIndicatorProps: {
            account,
            report,
            open: true,
            onManualReconcile: () => {
              this.setState({ autoReconciliationLoadingIndicatorProps: null })
              onReconcileClick(formType, account, 'manual')
            },
            onCompleted: async (reconciliation) => {
              this.setState({ autoReconciliationLoadingIndicatorProps: null })
              if (reconciliation) {
                // Re-read against the row auto-recon just wrote; the store has not
                // caught up with it either.
                const refreshed = await this.refreshAfterReconciliationWrite()

                if (refreshed?.[account.id]?.canRecover) {
                  this.setState({
                    autoReconciliationRecoveryModalProps: {
                      userId,
                      reportId,
                      account,
                      bookkeepingReport: report,
                      reconciliation,
                      open: true,
                      close: onRecoverModalClose,
                      onManualReconcile: onRecoverModalManualReconcile,
                    },
                  })
                } else {
                  const virtualStatementData =
                    await this.props.getVirtualStatement(reconciliation.id)
                  if (virtualStatementData) {
                    this.setState({ virtualStatementData })
                  }
                }
              }
            },
          },
        })
      } else if (reconType === 'recover') {
        const reconciliation =
          account.reconciliations.find(
            (recon) =>
              recon.id ===
              capabilitiesByAccount[account.id]?.period.reconciliationId
          ) ?? null
        this.setState({
          autoReconciliationRecoveryModalProps: {
            userId,
            reportId,
            account,
            bookkeepingReport: report,
            reconciliation,
            open: true,
            close: onRecoverModalClose,
            onManualReconcile: onRecoverModalManualReconcile,
          },
        })
      } else {
        this.setState({
          reconciliationModalProps: {
            account,
            formType,
            open: true,
            userId: user?.id,
            bookkeepingReport: report,
            currentReconciliation:
              account.reconciliations.find(
                (recon) =>
                  recon.id ===
                  capabilitiesByAccount[account.id]?.period.reconciliationId
              ) ?? null,
            close: () => {
              this.setState({ reconciliationModalProps: null })
            },
            onSumMatch: async () => {
              await this.refreshAfterReconciliationWrite()
            },
            toggleVirtualStatement: this.toggleVirtualStatement,
          },
        })
      }
    }

    return (
      <>
        {reconcileLocked && (
          <Alert type="warn" style={{ marginBottom: 10 }}>
            You must &quot;Re-start Books&quot; before making any changes.
            <br />
            <Text as="bodySm">
              Books closed
              {lockedAt &&
                ` on ${formatISOFromUTC(lockedAt, DATE_FORMATS_LUXON.DISPLAY_SHORT)}`}
            </Text>
          </Alert>
        )}
        <Grid stackable doubling>
          <Grid.Row stretched>
            <Grid.Column width={6}>
              {userId && <StatementCard userId={userId} report={report} />}
            </Grid.Column>
            <Grid.Column width={4}>
              <TransactionCountsCard
                userId={userId}
                reportId={reportId}
                loading={loading}
                transactionChangeCount={transactionChangeCount}
              />
            </Grid.Column>
            <Grid.Column width={6}>
              {report && <BookkeepingStatusCard report={report} />}
            </Grid.Column>
          </Grid.Row>
        </Grid>

        <Divider />
        {report && (
          <AccountSumCards
            userId={userId}
            financialAccounts={accounts}
            newTransactionCounts={newTransactionCounts}
            capabilitiesByAccount={capabilitiesByAccount}
            capabilitiesInFlight={capabilitiesInFlight}
            onReconcileClick={onReconcileClick}
            toggleVirtualStatement={this.toggleVirtualStatement}
          />
        )}
        {reconciliationModalProps && (
          <ReconciliationModal {...reconciliationModalProps} />
        )}
        {autoReconciliationLoadingIndicatorProps && (
          <AutoReconciliationLoadingIndicator
            {...autoReconciliationLoadingIndicatorProps}
          />
        )}
        {autoReconciliationRecoveryModalProps && (
          <AutoReconciliationRecoveryModal
            {...autoReconciliationRecoveryModalProps}
          />
        )}
        <Divider />

        <Dimmer.Dimmable dimmed={bookkeepingComplete}>
          <Dimmer active={bookkeepingComplete} verticalAlign="top">
            <Text as="h2" style={{ color: '#ffffff' }}>
              You must &quot;Re-start Books&quot; to view transactions.
            </Text>
          </Dimmer>

          {user && (
            <PaginatedTransactionTable
              user={user}
              onTransactionChange={onTransactionChange}
            />
          )}
        </Dimmer.Dimmable>

        <VirtualStatement
          toggleOpen={() => this.toggleVirtualStatement()}
          onReconcileClick={onReconcileClick}
          data={this.state.virtualStatementData}
          reconcileLocked={reconcileLocked}
        />
      </>
    )
  }

  renderPnL() {
    const {
      report,
      params: { userId },
    } = this.props
    if (!userId) {
      return null
    }

    return (
      <ProfitAndLossTable
        startDate={report?.startDate}
        endDate={report?.endDate}
        userId={userId}
      />
    )
  }

  render() {
    const {
      user,
      report,
      status,
      params: { userId, reportId },
    } = this.props

    const membership = user?.memberships?.length
      ? user.memberships.filter((membership) => membership.isPrimary)[0]
      : null

    return (
      <Container id="adminPanel" fluid>
        <UserBreadcrumbs userId={userId} pageName="Monthly Books">
          <NewBreadcrumb.Item>
            <NewBreadcrumb.Link
              onClick={() => {
                window.location.href = `/admin/finances/records/${userId}`
              }}
            >
              {DateTime.fromISO(report?.date ?? '').toFormat(
                DATE_FORMATS_LUXON.MONTH_YEAR
              )}
            </NewBreadcrumb.Link>
          </NewBreadcrumb.Item>
        </UserBreadcrumbs>
        <UserFreeTrialAlert userId={userId} />
        <Divider />
        <UserRecordHeader user={user} />
        <div className="flex flex-row items-center w-full my-4">
          <NewText variant={TypographyStyles.H1} className="mr-4">
            <Link to={`/admin/finances/records/${userId}`}>
              <Button circular icon="arrow left" />
            </Link>
            {DateTime.fromISO(report?.date ?? '').toFormat(
              DATE_FORMATS_LUXON.MONTH_YEAR
            )}{' '}
            Books: {user?.firstName} {user?.lastName}
          </NewText>
          {membership?.status === 'unpaid' ? (
            <Badge color="red">Unpaid</Badge>
          ) : (
            <BookkeepingStatusLabel reportId={reportId} />
          )}
          {report?.statusAutomationDisabledAt && (
            <Badge color="yellow" className="ml-2">
              Status Automation Disabled
            </Badge>
          )}
        </div>
        <Separator />
        <div className="w-full">{this.renderActionsBar()}</div>
        <br />
        <div
          style={{
            display: 'flex',
            flexDirection: 'row-reverse',
          }}
        >
          <Menu secondary size="mini">
            <Menu.Menu>
              <Menu.Item>
                <Button
                  size="mini"
                  className="headerButton"
                  secondary
                  onClick={() =>
                    this.handleBooksUpdate(BookkeepingReportStatus.plaid_error)
                  }
                  disabled={status === BookkeepingReportStatus.plaid_error}
                >
                  Mark Plaid Error
                </Button>
              </Menu.Item>
              <Menu.Item>
                <Button
                  size="mini"
                  className="headerButton"
                  secondary
                  onClick={() =>
                    this.handleBooksUpdate(BookkeepingReportStatus.missing_info)
                  }
                  disabled={status === BookkeepingReportStatus.missing_info}
                >
                  Mark Missing Information
                </Button>
              </Menu.Item>
              <DownloadPayrollReportMenuItem
                reportId={reportId}
                userId={userId}
              />
            </Menu.Menu>
          </Menu>
        </div>
        <br />
        {membership?.status === 'unpaid' && (
          <Alert type="error">
            This user&apos;s account is defunct — do not work on their books.
          </Alert>
        )}
        <br />

        {report ? (
          <Tab
            menu={{ secondary: true, pointing: true }}
            panes={[
              { menuItem: 'Books', render: () => this.renderBooks() },
              { menuItem: 'Profit and Loss', render: () => this.renderPnL() },
              {
                menuItem: 'User Record Notes',
                render: () =>
                  userId && <UserRecordNotes userId={Number(userId)} />,
              },
              {
                menuItem: 'Onboarding',
                render: () => <OnboardingAnswers userId={Number(userId)} />,
              },
            ]}
          />
        ) : (
          <Dimmer active inverted>
            <Loader inverted>Loading report</Loader>
          </Dimmer>
        )}
      </Container>
    )
  }
}

const mapStateToProps = (state: ReduxState, props: ParentProps) => ({
  user: getUserById(state, props.params.userId),
  report: selectBookkeepingReportsById(state, props.params.reportId),
  accounts: selectSortedFinancialAccounts(state, props.params.userId),
  status: selectBookkeepingReportStatus(state, props.params.reportId),
  updateStatusError: getFetchError(
    state,
    FETCH_UPDATE_BOOKKEEPING_REPORT_STATUS_KEY
  ),
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  fetchTransactionCounts: wrapDispatch(dispatch, fetchTransactionCounts),
  clearTransactionFilters: wrapDispatch(dispatch, clearTransactionFilters),
  clearAndUpdateTransactionFilters: wrapDispatch(
    dispatch,
    clearAndUpdateTransactionFilters
  ),
  fetchAdminUsersBookkeepingReports: wrapDispatch(
    dispatch,
    fetchAdminUsersBookkeepingReports
  ),
  updateUsersBookkeepingReportStatus: wrapDispatch(
    dispatch,
    updateUsersBookkeepingReportStatus
  ),
  fetchAdminUsersDocuments: wrapDispatch(dispatch, fetchAdminUsersDocuments),
  adminFetchSingleUser: wrapDispatch(dispatch, adminFetchSingleUser),
  fetchUserFinancialAccounts: wrapDispatch(
    dispatch,
    fetchUserFinancialAccounts
  ),
  fetchStaleTransactionCountsForReport: wrapDispatch(
    dispatch,
    fetchStaleTransactionCountsForReport
  ),
  fetchTransactionCountsSinceLastReconciliation: wrapDispatch(
    dispatch,
    fetchTransactionCountsSinceLastReconciliation
  ),
  fetchReconciliationCapabilities: wrapDispatch(
    dispatch,
    fetchReconciliationCapabilities
  ),
  getVirtualStatement: wrapDispatch(dispatch, getVirtualStatement),
})

const ConnectedMonthlyBooksPanel = connect<
  StateProps,
  DispatchProps,
  ParentProps,
  ReduxState
>(
  mapStateToProps,
  mapDispatchToProps
)(MonthlyBooksPanel)

export default withRouter(ConnectedMonthlyBooksPanel)
